import React, { useState, useEffect, useMemo, ChangeEvent, CompositionEvent } from 'react'
import classes from '../Sections.module.scss'
import ExpandableSection from "../ExpandableSection"
import InputField from "../../../shared/ComponentLibrary/RInputField"
import SelectInputField from "../../../shared/ComponentLibrary/RSelectInputField"
import { SelectChangeEvent } from "@mui/material";

type ProjectProps = {
  index: number
  project: UserProject
  register: any
  practiceAreaListData: PracticeAreaList
  industryListData: IndustryList
  softwareProductListData: SoftwareProductList
  responsibilityListData: ResponsibilityList
  methodologiesListData: MethodologyList
  languageListData: LanguageList
  expand: boolean
  onCloseHandler: () => void
  hasChanged?: (() => boolean) | undefined
  setHasChanged?: ((e: SelectChangeEvent<any> | ChangeEvent<HTMLInputElement>) => void)
}


const Project = ({ index, project, register, practiceAreaListData, industryListData, softwareProductListData, responsibilityListData, methodologiesListData, languageListData, expand, onCloseHandler, hasChanged, setHasChanged }: ProjectProps) => {
  var currentIndustry: DropDownOption
  currentIndustry = industryListData.industryList?.find(({ value }) => value == (project.industry && project.industry.value !== '' ? project.industry.value : project.industryId)) as DropDownOption
  var currentPracticeArea: DropDownOption
  currentPracticeArea = practiceAreaListData.practiceAreasList?.find(({ value }) => value == (project.practiceArea && project.practiceArea.value !== '' ? project.practiceArea.value : project.practiceAreaId)) as DropDownOption

  var currentReviewMethodology: DropDownOption
  currentReviewMethodology = methodologiesListData.methodologyList?.find(({ value }) => value == (project.reviewMethodology && project.reviewMethodology.value !== '' ? project.reviewMethodology.value : project.reviewMethodologyId)) as DropDownOption
  const projectId = project.id || ''

  const [changedPracticeAreaId, setChangedPracticeAreaId] = useState<number | undefined>(project.practiceArea && project.practiceArea.value)
  const [changedIndustryId, setChangedIndustryId] = useState<number | undefined>(project.industry && project.industry.value)
  const [changedDurationMonthsTimer, setChangedDurationMonthsTimer] = useState<number | undefined>(undefined)
  const [delayedChangedDurationMonths, setDelayedChangedDurationMonths] = useState<string>(project.durationMonths?.toString() ?? '0')
  const [changedSoftwareProductId, setChangedSoftwareProductId] = useState<number | undefined>(project.softwareProduct && project.softwareProduct.value)
  const [changedResponsibilities, setChangedResponsibilities] = useState<string[]>(project.responsibilities)
  const [changedReviewMethodologyId, setChangedReviewMethodologyId] = useState<number | undefined>(project.reviewMethodology && project.reviewMethodology.value)

  const [changedLanguages, setChangedLanguages] = useState<string[]>(project.languages?.map((l) => l.value))
  const showProjectAsValid = useMemo(() => shouldShowProjectAsValid(), [changedPracticeAreaId, changedIndustryId, delayedChangedDurationMonths, changedSoftwareProductId, changedResponsibilities, changedLanguages, changedReviewMethodologyId, onCloseHandler]);
  function shouldShowProjectAsValid() {
    if (!hasChanged?.()) {
      return project.isValid
    }
    if (changedPracticeAreaId === null) {
      return false
    }
    if (changedIndustryId === null) {
      return false
    }
    if (!(/^\s*\d+\s*$/.test(delayedChangedDurationMonths))) {
      return false
    }
    if (parseInt(delayedChangedDurationMonths, 10) <= 0) {
      return false
    }
    if (changedSoftwareProductId === null) {
      return false
    }
    if (changedResponsibilities && changedResponsibilities.length === 0) {
      return false
    }
    if (changedLanguages && changedLanguages.length === 0) {
      return false
    }
    return true
  }

  function durationMonthsOnChange(e: ChangeEvent<HTMLInputElement>) {
    if (typeof changedDurationMonthsTimer === 'number') {
      window.clearTimeout(changedDurationMonthsTimer);
    }
    let newTimer = window.setTimeout(() => {
      setDelayedChangedDurationMonths(e.target.value);
      setHasChanged?.(e)
      setChangedDurationMonthsTimer(undefined);
    }, 100);
    setChangedDurationMonthsTimer(newTimer);
  }
  
  function isValidDurationMonthsCharPerformant(char: string){
    if (char >= '0' && char <= '9'){
      return true;
    }
    return false;
  }
  
  function durationMonthsOnCompose(e: CompositionEvent<HTMLInputElement>) {
    let inputData = e.data;
    if (inputData === null || inputData.length === 0) {
      return;
    }
    if (inputData.length === 1){
      if (!isValidDurationMonthsCharPerformant(inputData)){
        e.preventDefault();
      }
      return;
    }
    if (inputData !== null && [...inputData].some(x => !isValidDurationMonthsCharPerformant(x))){
      e.preventDefault();
    }
  }

  const handleClose = () => {
    const isValid = shouldShowProjectAsValid();
    console.log('Valid?', isValid);
    // Optionally pass the validation result up:
    onCloseHandler();
  };

  return (
    <ExpandableSection heading={`${hasChanged?.() ? '*' : ''}Project ${index + 1}: ${currentPracticeArea ? currentPracticeArea.name + ' - ' : ''} ${currentIndustry ? currentIndustry.name : ''}`} onCloseHandler={handleClose} expand={expand} project={project} showProjectAsValid={showProjectAsValid}>
      {
        project.id && (
          <div className={classes.input_field}>
            <InputField
              name='id'
              defaultValue={projectId}
              hidden={true}
              register={{ ...register(`projects[${index}][id]`) }}
            />
          </div>
        )
      }
      <div className={classes.side_by_side}>
        <div className={classes.input_field}>
          {
            responsibilityListData.responsibilityList &&
            <SelectInputField
              name='Review Methodology'
              label='Review Methodology'
              value={project.reviewMethodology && project.reviewMethodology.value}
              privacy='allow'
              size='small'
              register={{ ...register(`projects[${index}][reviewMethodologyId]`) }}
              itemsList={methodologiesListData?.methodologyList}
              onChange={(e) => { setChangedReviewMethodologyId(e.target.value); return setHasChanged?.(e) }}
            />
          }
        </div>
      </div>
      <div className={classes.side_by_side}>
        <div className={classes.input_field}>
          {
            practiceAreaListData.practiceAreasList &&
            <SelectInputField
              name='Practice Area'
              label='Practice Area'
              value={project.practiceArea && project.practiceArea.value}
              privacy='allow'
              size='small'
              register={{ ...register(`projects[${index}][practiceAreaId]`) }}
              itemsList={practiceAreaListData.practiceAreasList}
              onChange={(e) => { setChangedPracticeAreaId(e.target.value); return setHasChanged?.(e) }}
            />
          }
        </div>

        <div className={classes.input_field}>
          {
            industryListData.industryList &&
            <SelectInputField
              name='Industry'
              label='Industry'
              value={project.industry && project.industry.value}
              privacy='allow'
              size='small'
              register={{ ...register(`projects[${index}][industryId]`) }}
              itemsList={industryListData.industryList}
              onChange={(e) => { setChangedIndustryId(e.target.value); return setHasChanged?.(e) }}
            />
          }
        </div>
      </div>
      <div className={classes.side_by_side}>
        <div className={classes.input_field}>
          <InputField
            name='major'
            label='Duration (months)'
            value={delayedChangedDurationMonths}
            privacy='allow'
            register={{ ...register(`projects[${index}][durationMonths]`) }}
            size='small'
            onChange={(e) => {
              setDelayedChangedDurationMonths(e.target.value);
              durationMonthsOnChange(e);
            }}
            onCompose={durationMonthsOnCompose}
          />
        </div>
        <div className={classes.input_field}>
          {
            softwareProductListData.softwareProductList &&
            <SelectInputField
              name='Review Software'
              label='Review Software'
              value={project.softwareProduct && project.softwareProduct.value}
              privacy='allow'
              size='small'
              register={{ ...register(`projects[${index}][softwareProductId]`) }}
              itemsList={softwareProductListData.softwareProductList}
              onChange={(e) => { setChangedSoftwareProductId(e.target.value); return setHasChanged?.(e) }}
            />
          }
        </div>
      </div>
      <div className={classes.side_by_side}>
        <div className={classes.input_field}>
          {
            responsibilityListData.responsibilityList &&
            <SelectInputField
              name='Project Responsibilities'
              label='Project Responsibilities'
              value={project?.responsibilities}
              privacy='allow'
              size='small'
              register={{ ...register(`projects[${index}][responsibilities]`) }}
              itemsList={responsibilityListData.responsibilityList}
              multiple
              withCheckboxes
              onChange={(e) => { setChangedResponsibilities(e.target.value); return setHasChanged?.(e) }}
            />
          }
        </div>
        <div className={classes.input_field}>
          {
            languageListData.languageList &&
            <SelectInputField
              label='Project Languages'
              name='Languages'
              register={{ ...register(`projects[${index}][languageIds]`) }}
              privacy='allow'
              size="small"
              value={project?.languages?.map((l) => l.value)}
              itemsList={languageListData.languageList}
              multiple
              withCheckboxes
              onChange={(e) => { setChangedLanguages(e.target.value); return setHasChanged?.(e) }}
            />
          }
        </div>
      </div>
    </ExpandableSection>
  )
}

export default Project
